import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  IonButton,
  IonImg,
  IonInput,
  IonItem,
  IonList,
  IonListHeader,
  IonText,
  useIonViewWillEnter,
} from '@ionic/react';
import { PageLayout } from 'containers/layout';
import AppActions from 'redux/AppRedux';
import { Clipboard } from '@capacitor/clipboard';
import { WaveIdManager } from '../sdk/WakeIdManager';

const ConstantVal = {
  DEFAULT_RSSI_LEVEL: '-50',
  DEFAULT_CARD_ID: '98765',
  DEVICE_NAME: 'RFIDeas',
  UUID_RF_IDEAS_SERVICE_ID: '0000A800-0000-1000-8000-00805F9B0C27',
  WRITE_CHARACTERISTIC_UUID: '0000AA00-0000-1000-8000-00805F9B0C27',
  READ_CHARACTERISTIC_UUID: '0000AC00-0000-1000-8000-00805F9B0C27',
  MANUFACTURER_DATA: {
    android: [2, 1, 6, 17, 7, 39, 12, 155, 95, 128, 0, 0, 128, 0, 16, 0, 0, 0, 168, 0, 0, 2, 10, 0, 21, 255, 2, 255, 0, 17, 34, 51, 68, 85, 102, 119,
      136, 153, 170, 187, 204, 221, 238, 255, 0, 1, 8, 9, 82, 70, 73, 68, 101, 97, 115, 0, 0, 0, 0, 0, 0, 0],
    ios: [2, 255, 0, 17, 34, 51, 68, 85, 102, 119, 136, 153, 170, 187, 204, 221, 238, 255, 0, 1]
  },
};

const MAX_SCAN_TIME = 30;

function BluetoothTestPage() {
  const dispatch = useDispatch();
  let BleManager;

  const peripherals = new Map();
  const [statusMsgs, setStatusMsgs] = useState('');
  const [cardId, setCardId] = useState(ConstantVal.DEFAULT_CARD_ID);
  const [rssi, setRssi] = useState(ConstantVal.DEFAULT_RSSI_LEVEL);
  const [listData, setListData] = useState([]);
  const [authData, setAuthData] = useState({});
  const [logs, setLogs] = useState('');

  if (!BleManager) {
    BleManager = new WaveIdManager(
      ConstantVal.DEVICE_NAME,
      ConstantVal.UUID_RF_IDEAS_SERVICE_ID,
      ConstantVal.WRITE_CHARACTERISTIC_UUID,
      ConstantVal.MANUFACTURER_DATA,
      ConstantVal.READ_CHARACTERISTIC_UUID,
      setLogs
    );
  }

  useIonViewWillEnter(async () => {

  });

  /**
   *use for `bluetooth` component initialization
   */
  const init = async () => {
    try {
      setRssi(ConstantVal.DEFAULT_RSSI_LEVEL);

      await BleManager.checkPermission();
      await BleManager.enableBluetooth();
      await BleManager.enableLocation();
      await BleManager.init();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    init();

    return () => {
      BleManager.destroy();
    };
  }, []);

  const cardIdChange = (e) => {
    const temp = e.target.value.replace(/[^0-9a-zA-Z]/g, '');
    setCardId(temp);
  };

  /**
   *its authenticates the device
   * @param {*} peripheral
   */
  const authDevice = async (peripheral) => {
    try {
      setStatusMsgs('Device found');

      const auth = await BleManager.authPeripheral(peripheral, cardId);

      peripherals.clear();
      peripherals.set(peripheral.id, peripheral);
      setListData(Array.from(peripherals.values()));

      setAuthData(auth || {});

      setStatusMsgs(auth?.success ? 'Authenticated.' : 'Authentication Failed.');

      await new Promise((resolve1) => setTimeout(resolve1, 1000));
    } catch (e) {
      setStatusMsgs(e || 'Something went wrong. Please try again.');

      await new Promise((resolve1) => setTimeout(resolve1, 1000));
    }
  };


  const startScanNew = async (rssiValue) => {
    if (cardId !== '') {
      setStatusMsgs('Scanning');

      const data = await BleManager.startScan(MAX_SCAN_TIME, rssiValue);
      if (data?.flag) {
        await authDevice(data.peripheral);
      } else {
        setStatusMsgs('Device not found.');

        await new Promise((resolve1) => setTimeout(resolve1, 1000));
      }
    } else {
      dispatch(
        AppActions.setToast(
          'Please enter a Card ID to proceed.',
          'danger'
        )
      );
    }
  };

  return (
    <PageLayout
      back
      title="Bluetooth Test"
      footer={
        <IonList>
          <IonItem lines='none'><IonText color="danger">{`Status: ${statusMsgs}`}</IonText></IonItem>

          <IonButton
            className="mb"
            color="danger"
            expand="block"
            fill="solid"
            onClick={() => Clipboard.write({
              string: logs,
            })}
          >
            Copy Logs
          </IonButton>

          <IonButton
            color="tertiary"
            expand="block"
            fill="solid"
            onClick={() => startScanNew(rssi)}
          >
            SCAN &amp; CONNECT
          </IonButton>
        </IonList>
      }
    >
      <IonList>
        <IonImg src="/assets/waveid_rfideas_logo.png" className="waveid-rf-logo" />
        <IonImg src="/assets/waveid_logo.png" className="waveid-header-logo" />

        <IonListHeader>Enter a Card ID on the textbox (or accept the default), and tap the Scan and connect button below</IonListHeader>

        <IonItem className='mt-2x'>
          <IonInput
            disabled={false}
            onIonChange={cardIdChange}
            value={cardId}
            type="number"
            placeholder="Card ID"
          />
        </IonItem>

        {

          <IonList>
            <IonItem lines='none'>
              <pre>
                {logs}
              </pre>
            </IonItem>
          </IonList>

        }

        {
          listData.length > 0 && (
            <IonList>
              {
                listData.map((item) => (
                  <IonList className='mb-2x'>
                    <IonItem lines='none'>
                      <IonText color='danger'><h4>Device Connected</h4></IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText color='tertiary'>Device Name: {item.name}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>RSSI: {item.rssi}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>Id: {item.id}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>EncodeByte: {authData.encodedData}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>Read: {authData.readValue}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>Write: {authData.writeValue}</IonText>
                    </IonItem>

                    <IonItem lines='none'>
                      <IonText>Validcodes: {authData.validcodes}</IonText>
                    </IonItem>
                  </IonList>
                ))
              }
            </IonList>
          )
        }
      </IonList>
    </PageLayout>
  );
}

export default BluetoothTestPage;
